import {
  Col,
  Row,
  Skeleton,
  Form,
  Input,
  InputNumber,
  Switch,
} from "antd";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Get } from "../../config/api/get";
import { Put } from "../../config/api/put";
import { Post } from "../../config/api";
import { PLAN } from "../../config/constants/api";
import Swal from "sweetalert2";
import { IoIosArrowRoundBack } from "react-icons/io";
import { FaGripVertical, FaTrash, FaPlus, FaEdit, FaCheck, FaTimes } from "react-icons/fa";

const EditPackage = () => {
  const token = useSelector((state) => state.user.userToken);
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [form] = Form.useForm();
  const [draggedFeatureIndex, setDraggedFeatureIndex] = useState(null);
  const [dragOverFeatureIndex, setDragOverFeatureIndex] = useState(null);
  const [newFeatureText, setNewFeatureText] = useState("");
  const [editingFeatureIndex, setEditingFeatureIndex] = useState(null);
  const [editingFeatureText, setEditingFeatureText] = useState("");

  // Convert features from backend format (objects with {text, index}) to strings array
  const convertFeaturesToStrings = (features) => {
    if (!features || !Array.isArray(features)) return [];
    // Sort by index first if they have index property
    const sorted = [...features].sort((a, b) => {
      const indexA = typeof a === 'object' && a && a.index !== undefined ? a.index : 999;
      const indexB = typeof b === 'object' && b && b.index !== undefined ? b.index : 999;
      return indexA - indexB;
    });
    // Convert to strings - handle both {text: "...", index: 0} objects and plain strings
    return sorted.map(f => {
      if (typeof f === 'string') return f;
      if (f && typeof f === 'object' && f.text) return f.text;
      return String(f || '');
    }).filter(f => f && f.trim().length > 0);
  };

  // Convert features from strings array to backend format (objects with index)
  const convertFeaturesToObjects = (features) => {
    if (!features || !Array.isArray(features)) return [];
    return features
      .filter(f => f && typeof f === 'string' && f.trim().length > 0)
      .map((text, index) => ({ text: text.trim(), index }));
  };

  useEffect(() => {
    getPlan();
    // eslint-disable-next-line
  }, [id]);

  const getPlan = () => {
    setLoading(true);
    Get(PLAN.getPlanById + id, token, {})
      .then((response) => {
        console.log("Full API Response:", response);
        // Handle different response structures
        let planData = null;
        
        // Check if response has success and data
        if (response?.success && response?.data) {
          planData = response.data;
        } 
        // Check if response.data exists (even without success field)
        else if (response?.data) {
          planData = response.data;
        } 
        // Check if response itself is the plan object (has _id)
        else if (response?._id) {
          planData = response;
        }
        // Check if response has a nested structure
        else if (response?.data?.data) {
          planData = response.data.data;
        }
        
        console.log("Extracted planData:", planData);
        
        if (planData && planData._id) {
          // Convert features to strings array
          const featuresAsStrings = convertFeaturesToStrings(planData.features);
          
          setPlan({ ...planData, features: featuresAsStrings });
          
          // Set form values
          form.setFieldsValue({
            title: planData.title,
            hours: planData.hours || 0,
            bookingDuration: planData.bookingDuration,
            savePercent: planData.savePercent,
            isPopular: planData.isPopular || false,
          });
          console.log("✅ Plan loaded, features:", featuresAsStrings);
          setLoading(false);
        } else {
          setLoading(false);
          console.log("Plan data not found. Response structure:", JSON.stringify(response, null, 2));
          Swal.fire({
            title: "Error!",
            text: "Plan not found. Check console for details.",
            icon: "error",
            confirmButtonColor: "#d33",
          }).then(() => {
            navigate("/membershipPackages");
          });
        }
      })
      .catch((err) => {
        console.log("Error Fetching Plan:", err);
        console.log("Error Response:", err?.response);
        setLoading(false);
        Swal.fire({
          title: "Error!",
          text: "Failed to load plan details",
          icon: "error",
          confirmButtonColor: "#d33",
        }).then(() => {
          navigate("/membershipPackages");
        });
      });
  };

  // Feature Drag and Drop Handlers
  const handleFeatureDragStart = (e, index) => {
    setDraggedFeatureIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target);
    e.target.style.opacity = "0.5";
  };

  const handleFeatureDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverFeatureIndex(index);
  };

  const handleFeatureDragLeave = () => {
    setDragOverFeatureIndex(null);
  };

  const handleFeatureDrop = async (e, dropIndex) => {
    e.preventDefault();
    setDragOverFeatureIndex(null);
    
    if (draggedFeatureIndex === null || draggedFeatureIndex === dropIndex) {
      setDraggedFeatureIndex(null);
      return;
    }

    // Get current features (already strings)
    const featuresArray = plan.features || [];

    // Optimistically update the UI - reorder strings
    const newFeatures = [...featuresArray];
    const draggedFeature = newFeatures[draggedFeatureIndex];
    newFeatures.splice(draggedFeatureIndex, 1);
    newFeatures.splice(dropIndex, 0, draggedFeature);
    
    setPlan({ ...plan, features: newFeatures });
    setDraggedFeatureIndex(null);

    // Send new order to backend (convert to objects with index)
    const featureTexts = newFeatures;
    try {
      const response = await Post(PLAN.reorderFeaturesByDrag, { planId: id, featureTexts }, token);
      if (response?.success || response?.status) {
        // Refresh to get updated features
        getPlan();
        Swal.fire({
          title: "Success!",
          text: response?.message || "Features reordered successfully",
          icon: "success",
          confirmButtonColor: "#3085d6",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        // Revert on error
        getPlan();
        Swal.fire({
          title: "Error!",
          text: response?.message || "Failed to reorder features",
          icon: "error",
          confirmButtonColor: "#d33",
        });
      }
    } catch (err) {
      // Revert on error
      getPlan();
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to reorder features. Please try again.";
      Swal.fire({
        title: "Error!",
        text: errorMessage,
        icon: "error",
        confirmButtonColor: "#d33",
      });
    }
  };

  const handleFeatureDragEnd = (e) => {
    e.target.style.opacity = "1";
    setDraggedFeatureIndex(null);
    setDragOverFeatureIndex(null);
  };

  const handleAddFeature = () => {
    if (!newFeatureText.trim()) {
      Swal.fire({
        title: "Error!",
        text: "Please enter a feature text",
        icon: "error",
        confirmButtonColor: "#d33",
      });
      return;
    }

    // Get current features (already strings)
    const featuresArray = plan.features || [];

    // Check if feature already exists
    const featureExists = featuresArray.some(f => 
      f.trim().toLowerCase() === newFeatureText.trim().toLowerCase()
    );

    if (featureExists) {
      Swal.fire({
        title: "Error!",
        text: "This feature already exists",
        icon: "error",
        confirmButtonColor: "#d33",
      });
      return;
    }

    // Add new feature as string
    const updatedFeatures = [...featuresArray, newFeatureText.trim()];
    
    setPlan({ ...plan, features: updatedFeatures });
    setNewFeatureText("");
  };

  const handleEditFeature = (featureIndex) => {
    // Get current features (already strings)
    const featuresArray = plan.features || [];
    const featureText = featuresArray[featureIndex] || '';
    
    setEditingFeatureIndex(featureIndex);
    setEditingFeatureText(featureText);
  };

  const handleSaveFeatureEdit = (featureIndex) => {
    if (!editingFeatureText.trim()) {
      Swal.fire({
        title: "Error!",
        text: "Feature text cannot be empty",
        icon: "error",
        confirmButtonColor: "#d33",
      });
      return;
    }

    // Get current features (already strings)
    const featuresArray = plan.features || [];

    // Check if edited text already exists (excluding current feature)
    const featureExists = featuresArray.some((f, idx) => {
      if (idx === featureIndex) return false;
      return f.trim().toLowerCase() === editingFeatureText.trim().toLowerCase();
    });

    if (featureExists) {
      Swal.fire({
        title: "Error!",
        text: "This feature already exists",
        icon: "error",
        confirmButtonColor: "#d33",
      });
      return;
    }

    // Update the feature as string
    const updatedFeatures = [...featuresArray];
    updatedFeatures[featureIndex] = editingFeatureText.trim();
    
    setPlan({ ...plan, features: updatedFeatures });
    setEditingFeatureIndex(null);
    setEditingFeatureText("");
  };

  const handleCancelFeatureEdit = () => {
    setEditingFeatureIndex(null);
    setEditingFeatureText("");
  };

  const handleDeleteFeature = (featureIndex) => {
    // Get current features (already strings)
    const featuresArray = plan.features || [];
    const featureText = featuresArray[featureIndex] || '';

    Swal.fire({
      title: "Are you sure?",
      html: `
        <p>You are about to delete the feature:</p>
        <p><strong>${featureText}</strong></p>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        // Remove feature (features are strings, so just filter)
        const updatedFeatures = featuresArray.filter((_, idx) => idx !== featureIndex);
        setPlan({ ...plan, features: updatedFeatures });
      }
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      console.log("Form values:", values);
      
      // Convert features from strings to objects for backend
      const formattedFeatures = convertFeaturesToObjects(plan.features);
      
      // Backend expects hours directly
      const updateData = {
        ...values,
        features: formattedFeatures,
      };
      console.log("Update data:", updateData);
      console.log("API endpoint:", PLAN.updatePlan + id);

      // Show loading
      Swal.fire({
        title: "Updating...",
        text: "Please wait while we update the plan.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const response = await Put(PLAN.updatePlan + id, token, updateData);
        console.log("API Response:", response);
        
        Swal.close();
        
        if (response?.status || response?.success) {
          Swal.fire({
            title: "Success!",
            text: response?.message || "Plan updated successfully.",
            icon: "success",
            confirmButtonColor: "#3085d6",
          }).then(() => {
            navigate(`/membershipPackages/view/${id}`);
          });
        } else {
          Swal.fire({
            title: "Error!",
            text: response?.message || "Failed to update plan.",
            icon: "error",
            confirmButtonColor: "#d33",
          });
        }
      } catch (apiError) {
        console.error("API Error:", apiError);
        console.error("API Error Response:", apiError?.response);
        Swal.close();
        const errorMessage = apiError?.response?.data?.message || apiError?.message || "Failed to update plan. Please try again.";
        Swal.fire({
          title: "Error!",
          text: errorMessage,
          icon: "error",
          confirmButtonColor: "#d33",
        });
      }
    } catch (validationError) {
      console.error("Validation failed:", validationError);
      Swal.close();
      Swal.fire({
        title: "Validation Error!",
        text: "Please fill in all required fields correctly.",
        icon: "error",
        confirmButtonColor: "#d33",
      });
    }
  };

  if (loading) {
    return (
      <Row justify="center">
        <Col md={23} xs={22}>
          <div
            className="bg-white rounded-lg sm:py-16 py-8 my-12"
            style={{ boxShadow: "1px 1px 1px 1px rgba(0, 0, 0, 0.05)" }}
          >
            <Skeleton active />
          </div>
        </Col>
      </Row>
    );
  }

  if (!plan) {
    return null;
  }

  return (
    <Row justify="center">
      <Col md={23} xs={22}>
        <div
          className="bg-white rounded-lg sm:py-16 py-8 my-12"
          style={{ boxShadow: "1px 1px 1px 1px rgba(0, 0, 0, 0.05)" }}
        >
          <div className="flex justify-between mx-1 lg:mx-8 mb-4">
            <div className="flex justify-center items-center">
              <IoIosArrowRoundBack
                onClick={() => navigate(`/membershipPackages/view/${id}`)}
                className="self-center cursor-pointer mr-1 w-[48px] h-[48px] max-sm:w-[24px] max-sm:h-[24px] text-black"
              />
              <h3 className="font-tavi lg:text-3xl text-2xl max-sm:text-lg font-normal text-black">
                Edit Package
              </h3>
            </div>
          </div>

          <Row justify="center" className="p-5">
            <Col xs={24}>
              <div className="bg-[#EBEBEB] rounded-lg flex flex-col items-center p-5 justify-center">
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleSubmit}
                  className="w-full"
                >
                  <Row gutter={[16, 0]}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="title"
                        label={
                          <span className="font-euclid text-sm max-sm:text-xs text-black font-bold">
                            Title
                          </span>
                        }
                        rules={[{ required: true, message: "Please enter plan title" }]}
                      >
                        <Input 
                          placeholder="e.g., Monthly Membership" 
                          size="large"
                          disabled={plan?.isDefault}
                          className="font-euclid text-sm max-sm:text-xs"
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item
                        name="hours"
                        label={
                          <span className="font-euclid text-sm max-sm:text-xs text-black font-bold">
                            Hours
                          </span>
                        }
                        rules={[
                          { required: true, message: "Please enter hours" },
                          { type: "number", min: 1, message: "Hours must be at least 1" },
                        ]}
                      >
                        <InputNumber
                          placeholder="e.g., 30"
                          size="large"
                          style={{ width: "100%" }}
                          min={1}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item
                        name="bookingDuration"
                        label={
                          <span className="font-euclid text-sm max-sm:text-xs text-black font-bold">
                            Booking Duration (hours)
                          </span>
                        }
                        rules={[
                          { required: true, message: "Please enter booking duration" },
                          {
                            type: "number",
                            min: 1,
                            message: "Duration must be at least 1 hour",
                          },
                        ]}
                      >
                        <InputNumber
                          placeholder="e.g., 8"
                          size="large"
                          style={{ width: "100%" }}
                          min={1}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item
                        name="savePercent"
                        label={
                          <span className="font-euclid text-sm max-sm:text-xs text-black font-bold">
                            Discount Percentage
                          </span>
                        }
                        rules={[
                          { required: true, message: "Please enter discount percentage" },
                          {
                            type: "number",
                            min: 0,
                            max: 100,
                            message: "Discount must be between 0 and 100",
                          },
                        ]}
                      >
                        <InputNumber
                          placeholder="e.g., 15"
                          size="large"
                          style={{ width: "100%" }}
                          min={0}
                          max={100}
                          addonAfter="%"
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item
                        name="isPopular"
                        label={
                          <span className="font-euclid text-sm max-sm:text-xs text-black font-bold">
                            Mark as Popular
                          </span>
                        }
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                    </Col>

                    <Col xs={24}>
                      <Form.Item
                        label={
                          <span className="font-euclid text-sm max-sm:text-xs text-black font-bold">
                            Features (Optional)
                          </span>
                        }
                      >
                        <div className="w-full">
                          {/* Add Feature Input */}
                          <div className="flex gap-2 mb-4">
                            <Input
                              size="large"
                              placeholder="Enter feature text"
                              value={newFeatureText}
                              onChange={(e) => setNewFeatureText(e.target.value)}
                              onPressEnter={handleAddFeature}
                              className="font-euclid text-sm max-sm:text-xs"
                            />
                            <button
                              type="button"
                              onClick={handleAddFeature}
                              className="flex justify-center items-center bg-[#9CFEC4] rounded-lg px-4 hover:bg-[#7FE5B0] transition-colors"
                              title="Add Feature"
                            >
                              <FaPlus className="text-[#089B44] h-[16px] w-[16px]" />
                            </button>
                          </div>
                          
                          {/* Draggable Features List */}
                          {plan?.features && Array.isArray(plan.features) && plan.features.length > 0 && (
                            <div className="mt-4">
                              <p className="font-euclid text-sm max-sm:text-xs text-black font-bold mb-3">
                                Drag to reorder features:
                              </p>
                              <div className="space-y-1.5">
                                {plan.features.map((featureText, index) => (
                                  <div
                                    key={`feature-${index}-${featureText}`}
                                    draggable
                                    onDragStart={(e) => handleFeatureDragStart(e, index)}
                                    onDragOver={(e) => handleFeatureDragOver(e, index)}
                                    onDragLeave={handleFeatureDragLeave}
                                    onDrop={(e) => handleFeatureDrop(e, index)}
                                    onDragEnd={handleFeatureDragEnd}
                                    className={`flex items-center gap-2 py-2 px-3 bg-gray-50 rounded-lg border border-gray-200 transition-all duration-200 ${
                                      draggedFeatureIndex === index ? 'opacity-50 bg-gray-100' : ''
                                    } ${
                                      dragOverFeatureIndex === index ? 'bg-blue-50 border-blue-300 border-t-2' : ''
                                    } hover:bg-gray-100 cursor-move`}
                                  >
                                    <div 
                                      className="flex items-center justify-center p-1 hover:bg-gray-200 rounded cursor-move"
                                      onMouseDown={(e) => e.stopPropagation()}
                                      title="Drag to reorder"
                                    >
                                      <FaGripVertical className="text-gray-500 text-base" />
                                    </div>
                                    <div className="flex-1 flex items-center gap-2">
                                      <span className="font-euclid text-xs text-gray-500">
                                        {index + 1}.
                                      </span>
                                      {editingFeatureIndex === index ? (
                                        <div className="flex-1 flex items-center gap-2">
                                          <Input
                                            size="small"
                                            value={editingFeatureText}
                                            onChange={(e) => setEditingFeatureText(e.target.value)}
                                            onPressEnter={() => handleSaveFeatureEdit(index)}
                                            className="font-euclid text-sm"
                                            autoFocus
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                          <div 
                                            className="flex justify-center items-center bg-[#9CFEC4] rounded-[100%] w-[26px] h-[26px] hover:bg-[#7FE5B0] transition-colors"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleSaveFeatureEdit(index);
                                            }}
                                            title="Save"
                                          >
                                            <FaCheck
                                              className="text-[#089B44] cursor-pointer h-[11px] w-[11px]"
                                            />
                                          </div>
                                          <div 
                                            className="flex justify-center items-center bg-[#FFE5E5] rounded-[100%] w-[26px] h-[26px] hover:bg-[#FFCCCC] transition-colors"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleCancelFeatureEdit();
                                            }}
                                            title="Cancel"
                                          >
                                            <FaTimes
                                              className="text-[#DC3545] cursor-pointer h-[11px] w-[11px]"
                                            />
                                          </div>
                                        </div>
                                      ) : (
                                        <p 
                                          className="font-euclid mb-0 text-sm text-gray-700 cursor-pointer hover:text-blue-600 flex-1"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditFeature(index);
                                          }}
                                          title="Click to edit"
                                        >
                                          {featureText}
                                        </p>
                                      )}
                                    </div>
                                    {editingFeatureIndex !== index && (
                                      <>
                                        <div 
                                          className="flex justify-center items-center bg-[#E3F2FD] rounded-[100%] w-[32px] h-[32px] hover:bg-[#BBDEFB] transition-colors"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditFeature(index);
                                          }}
                                          title="Edit"
                                        >
                                          <FaEdit
                                            className="text-[#1976D2] cursor-pointer h-[14px] w-[14px]"
                                          />
                                        </div>
                                        <div 
                                          className="flex justify-center items-center bg-[#FFE5E5] rounded-[100%] w-[32px] h-[32px] hover:bg-[#FFCCCC] transition-colors"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteFeature(index);
                                          }}
                                          title="Delete"
                                        >
                                          <FaTrash
                                            className="text-[#DC3545] cursor-pointer h-[14px] w-[14px]"
                                          />
                                        </div>
                                      </>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {(!plan?.features || !Array.isArray(plan.features) || plan.features.length === 0) && (
                            <div className="text-center py-4 text-gray-500 font-euclid text-sm">
                              No features added yet. Add features using the input above.
                            </div>
                          )}
                        </div>
                      </Form.Item>
                    </Col>
                  </Row>

                  <div className="flex flex-col items-center justify-center mt-6">
                    <button
                      type="submit"
                      className="uppercase bg-button py-4 px-6 xl:px-16 text-black rounded-lg font-bold text-sm max-md:text-[10px] max-md:py-2 max-md:px-4 font-euclid"
                    >
                      Update Package
                    </button>
                  </div>
                </Form>
              </div>
            </Col>
          </Row>
        </div>
      </Col>
    </Row>
  );
};

export default EditPackage;

